import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Styled Excel Export Utility
 * Produces a professional Excel file with:
 * - Company name header (centered, bold, teal)
 * - Report title (centered, bold)
 * - Date range + generated info
 * - Dark header row with white text
 * - Alternating row colors
 * - Yellow totals row with red bold text
 * - Thin borders, proper column widths, frozen header
 *
 * @param {Object} options
 * @param {string} options.reportTitle - Title displayed in the Excel (e.g., "STOCK SUMMARY REPORT")
 * @param {string} options.sheetName - Worksheet tab name (e.g., "Stock Summary")
 * @param {string} options.fileName - Output file name (e.g., "StockSummary_2024-01-01_2024-01-31.xlsx")
 * @param {string} options.companyName - Company name for header
 * @param {string} options.username - Username for generated-by info
 * @param {string} [options.fromDate] - Start date for date range display
 * @param {string} [options.toDate] - End date for date range display
 * @param {string} [options.dateRangeText] - Custom date range text (overrides fromDate/toDate)
 * @param {string[]} options.headers - Array of column header strings
 * @param {Array[]} options.rows - Array of row arrays (data rows)
 * @param {Array} [options.totalsRow] - Optional totals row array
 * @param {number[]} [options.columnWidths] - Optional array of column widths
 * @param {string[]} [options.extraInfo] - Optional extra info lines below date range
 * @param {Array[]} [options.rawRows] - If provided, uses raw pre-built rows (array-of-arrays) directly without styled headers. Useful for complex nested reports.
 */
export const exportStyledExcel = async ({
    reportTitle,
    sheetName,
    fileName,
    companyName,
    username,
    fromDate,
    toDate,
    dateRangeText,
    headers,
    rows,
    totalsRow,
    columnWidths,
    extraInfo,
    rawRows,
    sectionHeaders,
    summaryStartIndex,
    rightAlignColumns,
}) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName || 'Report');

    const now = new Date();
    const formattedDateTime = `${now.toLocaleDateString('en-IN')}, ${now.toLocaleTimeString('en-IN')}`;

    const thinBorder = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
    };

    // RAW ROWS MODE: For complex reports with pre-built row arrays
    if (rawRows && rawRows.length > 0) {
        const maxCols = rawRows.reduce((max, r) => Math.max(max, (r || []).length), 0);
        let currentRow = 1;

        // Row 1: Company Name
        worksheet.mergeCells(currentRow, 1, currentRow, maxCols || 6);
        const companyCell = worksheet.getCell(`A${currentRow}`);
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14, color: { argb: 'FF10586a' } };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;

        // Row 2: Report Title
        worksheet.mergeCells(currentRow, 1, currentRow, maxCols || 6);
        const titleCell = worksheet.getCell(`A${currentRow}`);
        titleCell.value = reportTitle || 'Report';
        titleCell.font = { bold: true, size: 12, color: { argb: 'FF333333' } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;

        // Row 3: Date Range
        const dateText = dateRangeText || (fromDate && toDate ? `Date Range : ${fromDate}  To : ${toDate}` : '');
        if (dateText) {
            worksheet.mergeCells(currentRow, 1, currentRow, maxCols || 6);
            const dateCell = worksheet.getCell(`A${currentRow}`);
            dateCell.value = dateText;
            dateCell.font = { size: 10, color: { argb: 'FF666666' } };
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            currentRow++;
        }

        // Row 4: Generated info
        worksheet.mergeCells(currentRow, 1, currentRow, maxCols || 6);
        const genCell = worksheet.getCell(`A${currentRow}`);
        genCell.value = `Generated : ${formattedDateTime} by ${username || ''}`;
        genCell.font = { size: 10, color: { argb: 'FF666666' } };
        genCell.alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;
        // Empty spacer
        worksheet.addRow([]);
        currentRow++;

        // Add all raw rows with basic styling
        const sectionSet = new Set(sectionHeaders || []);
        rawRows.forEach((rowData) => {
            if (!rowData || rowData.length === 0) {
                worksheet.addRow([]);
                currentRow++;
                return;
            }

            const firstCell = rowData[0];
            const isSectionHeader = sectionSet.has(firstCell);
            const isSubHeader = firstCell === 'Metric' || firstCell === 'Particulars' || firstCell === 'Description';

            const row = worksheet.addRow(rowData);

            if (isSectionHeader) {
                // Merge all columns for section header so full text is visible
                if (maxCols > 1) {
                    worksheet.mergeCells(currentRow, 1, currentRow, maxCols);
                }
                // Section heading style - colored background, bold white text
                row.eachCell({ includeEmpty: true }, (cell) => {
                    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF263078' } };
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    cell.border = thinBorder;
                });
            } else if (isSubHeader) {
                // Sub-header row (Metric | Value) - dark background, white text, centered
                row.eachCell({ includeEmpty: true }, (cell) => {
                    cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4a3728' } };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    cell.border = thinBorder;
                });
            } else {
                row.eachCell((cell) => {
                    cell.border = thinBorder;
                    cell.font = { size: 10 };
                    const val = cell.value;
                    if (typeof val === 'number') {
                        cell.alignment = { vertical: 'middle', horizontal: 'right' };
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    }
                });
            }
            currentRow++;
        });

        // Column widths
        if (columnWidths && columnWidths.length > 0) {
            worksheet.columns = columnWidths.map(w => ({ width: w }));
        } else {
            worksheet.columns = Array.from({ length: maxCols }, () => ({ width: 14 }));
        }

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
        return;
    }

    const colCount = headers.length;
    let currentRow = 1;

    // Row 1: Company Name
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const companyCell = worksheet.getCell(`A${currentRow}`);
    companyCell.value = companyName || 'Company Name';
    companyCell.font = { bold: true, size: 14, color: { argb: 'FF10586a' } };
    companyCell.alignment = { vertical: 'middle', horizontal: 'center' };
    currentRow++;

    // Row 2: Report Title
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const titleCell = worksheet.getCell(`A${currentRow}`);
    titleCell.value = reportTitle || 'Report';
    titleCell.font = { bold: true, size: 12, color: { argb: 'FF333333' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    currentRow++;

    // Row 3: Date Range
    const dateText = dateRangeText || (fromDate && toDate ? `Date Range : ${fromDate}  To : ${toDate}` : '');
    if (dateText) {
        worksheet.mergeCells(currentRow, 1, currentRow, colCount);
        const dateCell = worksheet.getCell(`A${currentRow}`);
        dateCell.value = dateText;
        dateCell.font = { size: 10, color: { argb: 'FF666666' } };
        dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;
    }

    // Row 4: Generated info
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const genCell = worksheet.getCell(`A${currentRow}`);
    genCell.value = `Generated : ${formattedDateTime} by ${username || ''}`;
    genCell.font = { size: 10, color: { argb: 'FF666666' } };
    genCell.alignment = { vertical: 'middle', horizontal: 'center' };
    currentRow++;

    // Extra info lines (if any)
    if (extraInfo && extraInfo.length > 0) {
        extraInfo.forEach(info => {
            worksheet.mergeCells(currentRow, 1, currentRow, colCount);
            const infoCell = worksheet.getCell(`A${currentRow}`);
            infoCell.value = info;
            infoCell.font = { size: 10, color: { argb: 'FF666666' } };
            infoCell.alignment = { vertical: 'middle', horizontal: 'center' };
            currentRow++;
        });
    }

    // Empty spacer row
    worksheet.addRow([]);
    currentRow++;

    const headerRowNum = currentRow;

    // Header row
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4a3728' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = thinBorder;
    });
    currentRow++;

    // Data rows
    rows.forEach((rowData, idx) => {
        const isSubtotalRow = rowData[0] === '__SUBTOTAL__';
        const isMergeRow = typeof rowData[0] === 'string' && rowData[0].startsWith('__MERGE__');
        const actualRowData = isSubtotalRow ? ['', ...rowData.slice(1)] : isMergeRow ? [rowData[0].replace('__MERGE__', ''), ...rowData.slice(1)] : rowData;
        const row = worksheet.addRow(actualRowData);
        const isSummaryRow = summaryStartIndex != null && idx >= summaryStartIndex;

        if (isMergeRow) {
            // Merge all columns for this header row
            if (colCount > 1) {
                worksheet.mergeCells(currentRow, 1, currentRow, colCount);
            }
            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.font = { bold: true, size: 11, color: { argb: 'FF004d40' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfbe9e7' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                cell.border = thinBorder;
            });
        } else if (isSubtotalRow) {
            // Style as subtotal row — bold, light orange background
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                cell.border = thinBorder;
                cell.font = { bold: true, size: 10, color: { argb: 'FF7B3F00' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDE8D8' } };
                const val = cell.value;
                const strVal = String(val || '').trim();
                const isNumericStr = strVal !== '' && !isNaN(strVal);
                if (typeof val === 'number' || isNumericStr) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    if (typeof val === 'number') cell.numFmt = '#,##0.00';
                } else if (strVal === 'Subtotal') {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });
        } else if (isSummaryRow) {
            // Style as totals/summary row
            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.border = thinBorder;
                cell.font = { bold: true, size: 10, color: { argb: 'FFDC2626' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
                const val = cell.value;
                if (typeof val === 'number') {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });
        } else {
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                cell.border = thinBorder;
                cell.font = { size: 10 };
                const val = cell.value;
                if (typeof val === 'number' || (rightAlignColumns && rightAlignColumns.includes(colNumber - 1))) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    if (typeof val === 'number') cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });
            // Alternating row background
            if (idx % 2 === 1) {
                row.eachCell({ includeEmpty: true }, (cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
            }
        }
        currentRow++;
    });

    // Totals row
    if (totalsRow && totalsRow.length > 0) {
        const tRow = worksheet.addRow(totalsRow);
        tRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = thinBorder;
            cell.font = { bold: true, size: 10, color: { argb: 'FFDC2626' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
            const val = cell.value;
            if (typeof val === 'number') {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
                cell.numFmt = '#,##0.00';
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
            }
        });
    }

    // Column widths
    if (columnWidths && columnWidths.length > 0) {
        worksheet.columns = columnWidths.map(w => ({ width: w }));
    } else {
        // Auto-calculate reasonable widths based on header text length
        worksheet.columns = headers.map(h => ({ width: Math.max((h || '').length + 4, 12) }));
    }

    // Freeze header row
    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRowNum }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
};
